import { Calendar, User as UserIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import type { Task } from '@coachapp/shared';
import DatePicker from '../components/DatePicker';
import { FormPageLayout } from '../components/layout/FormPageLayout';
import { useTransitionNavigate } from '../hooks/useTransitionNavigate';
import { getApiErrorMessage } from '../lib/api-error';
import { isArchivedClient } from '../lib/client-status';
import { fromDatetimeLocalValue, toDatetimeLocalValue } from '../lib/datetime-local';
import {
  useCreateTaskMutation,
  useGetClientsQuery,
  useGetTasksQuery,
  useUpdateTaskMutation,
} from '../store/coachApi';

interface TaskFormContentProps {
  initialData?: Task;
  defaultClientId?: string;
  onClose: () => void;
  onCreated: (clientId?: string) => void;
}

const TaskFormContent = ({ initialData, defaultClientId, onClose, onCreated }: TaskFormContentProps) => {
  const { t } = useTranslation();
  const [error, setError] = useState('');
  const { data: clients = [], isFetching: fetchingClients, error: clientsError } = useGetClientsQuery();
  const clientsLoading = fetchingClients && clients.length === 0;
  const [createTask, { isLoading: creating }] = useCreateTaskMutation();
  const [updateTask, { isLoading: updating }] = useUpdateTaskMutation();
  const loading = creating || updating;

  const [formData, setFormData] = useState({
    title: initialData?.title || '',
    clientId: initialData?.clientId || defaultClientId || '',
    description: initialData?.description || '',
    dueAt: initialData?.dueAt ? toDatetimeLocalValue(initialData.dueAt) : '',
  });

  const fetchError = clientsError
    ? getApiErrorMessage(clientsError, 'Не удалось загрузить список клиентов')
    : null;

  const selectableClients = useMemo(
    () => clients.filter((client) => !isArchivedClient(client)),
    [clients],
  );

  const resolvedClientId = useMemo(() => {
    if (!formData.clientId || clientsLoading) return '';
    return selectableClients.some((client) => client.id === formData.clientId)
      ? formData.clientId
      : '';
  }, [formData.clientId, selectableClients, clientsLoading]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (clientsLoading) return;
    setError('');

    const payload = {
      title: formData.title,
      clientId: resolvedClientId || undefined,
      description: formData.description || undefined,
      dueAt: formData.dueAt ? fromDatetimeLocalValue(formData.dueAt) : undefined,
    };

    try {
      if (initialData) {
        await updateTask({
          id: initialData.id,
          data: {
            title: payload.title,
            clientId: payload.clientId ?? null,
            description: payload.description,
            dueAt: payload.dueAt ?? null,
          },
        }).unwrap();
      } else {
        await createTask(payload).unwrap();
        onCreated(payload.clientId);
        return;
      }
      onClose();
    } catch (err) {
      setError(
        err && typeof err === 'object' && 'message' in err && typeof err.message === 'string'
          ? err.message
          : getApiErrorMessage(err, 'Ошибка при сохранении'),
      );
    }
  };

  const pageTitle = initialData ? t('Редактировать задачу') : t('Новая задача');

  return (
    <FormPageLayout title={pageTitle} onBack={onClose}>
      <form onSubmit={handleSubmit} className="space-y-5">
        {(error || fetchError) && (
          <div className="p-3 bg-red-500/10 border border-red-500/20 text-red-600 rounded-2xl text-center text-sm font-medium">
            {error || fetchError}
          </div>
        )}

        <div className="space-y-1.5">
          <label className="section-label ml-1">
            {t('Название')}
          </label>
          <input
            type="text"
            className="input-field font-medium"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            required
          />
        </div>

        <div className="space-y-1.5">
          <label className="section-label ml-1 flex items-center gap-1.5">
            <UserIcon size={12} /> {t('Клиент')}
          </label>
          <select
            className="input-field font-medium appearance-none cursor-pointer"
            value={resolvedClientId}
            onChange={(e) => setFormData({ ...formData, clientId: e.target.value })}
            disabled={clientsLoading}
          >
            <option value="">{t('Без клиента')}</option>
            {selectableClients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-1.5">
          <label className="section-label ml-1">
            {t('Описание')}
          </label>
          <textarea
            className="input-field font-medium min-h-[80px] resize-y"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            rows={3}
          />
        </div>

        <div className="space-y-1.5">
          <label className="section-label ml-1 flex items-center gap-1.5">
            <Calendar size={12} /> {t('Срок')}
          </label>
          <DatePicker
            mode="datetime"
            value={formData.dueAt}
            onChange={(dueAt) => setFormData({ ...formData, dueAt })}
            clearable
          />
        </div>

        <div className="flex flex-col-reverse sm:flex-row gap-3 pt-4">
          <button type="button" className="btn-secondary flex-1 min-h-12" onClick={onClose}>
            {t('Отмена')}
          </button>
          <button type="submit" className="btn-primary flex-1 sm:flex-[2] min-h-12" disabled={loading || clientsLoading}>
            {loading ? t('Сохранение...') : initialData ? t('Сохранить') : t('Создать задачу')}
          </button>
        </div>
      </form>
    </FormPageLayout>
  );
};

const TaskFormRoute = () => {
  const navigate = useTransitionNavigate();
  const location = useLocation();
  const { taskId } = useParams();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const defaultClientId = searchParams.get('clientId') || undefined;
  const locationState = location.state as { task?: Task } | null;
  const { data: tasks = [], isLoading } = useGetTasksQuery(undefined, {
    skip: !taskId || !!locationState?.task,
  });
  const initialData =
    locationState?.task ?? (taskId ? tasks.find((item) => item.id === taskId) : undefined);
  const detailLoading = !!taskId && !locationState?.task && isLoading;
  const close = () => navigate(-1);
  const afterCreate = (clientId?: string) => {
    if (clientId) {
      navigate(`/clients/${clientId}?tab=more&section=client-tasks`, { replace: true });
      return;
    }
    navigate('/tasks', { replace: true });
  };
  const pageTitle = initialData ? t('Редактировать задачу') : t('Новая задача');

  if (detailLoading) {
    return <FormPageLayout title={pageTitle} onBack={close} loading />;
  }

  return (
    <TaskFormContent
      key={initialData?.id ?? 'new'}
      initialData={initialData}
      defaultClientId={defaultClientId}
      onClose={close}
      onCreated={afterCreate}
    />
  );
};

export default TaskFormRoute;
